import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Layers, Users, TrendingUp, Building2, ArrowRight, Search, X, Crosshair, Eye, ChevronDown, ChevronUp, UserPlus, Gauge } from 'lucide-react';
import KpiSparkline from './KpiSparkline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };
const TAB_STORAGE_KEY = 'hub_portfolio_tab_v1';

/**
 * PortfolioPanel — right-side panel split in two tabs (Projets default, Contrôle).
 * - Projets : compact single-line rows with expand → description, dates, action buttons.
 * - Contrôle : KPIs (projects / lots / pipeline / leads) with sparklines.
 * Tab selection is persisted to localStorage so the user lands where they left off.
 */
const PortfolioPanel = ({
  projects = [],
  selectedId,
  onSelectProject,
  onDeselectProject,
  onNavigateDetails,
  onNavigateMinisite,
  onClose,
  visible = true,
  filter = 'all',
  setFilter,
  search: searchProp,
  setSearch: setSearchProp,
}) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem(TAB_STORAGE_KEY) || 'projets'; }
    catch { return 'projets'; }
  });
  useEffect(() => {
    try { localStorage.setItem(TAB_STORAGE_KEY, tab); } catch { /* ignore */ }
  }, [tab]);
  const [kpis, setKpis] = useState({ leads: 0, pipeline: 0, opps: 0 });
  const [trends] = useState({
    projects:  [4, 5, 5, 6, 6, 6, 6],
    lots:      [220, 240, 260, 280, 295, 305, 314],
    pipeline:  [1.6, 1.7, 1.85, 2.0, 2.15, 2.30, 2.42],
    leads:     [2, 3, 3, 5, 6, 6, 7],
  });
  // Controlled OR uncontrolled search (defensive: parent may or may not pass it)
  const [localSearch, setLocalSearch] = useState('');
  const search = typeof searchProp === 'string' ? searchProp : localSearch;
  const setSearch = setSearchProp || setLocalSearch;
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/crm/contacts?limit=200`, opts).catch(() => ({ data: { items: [], total: 0 } })),
      axios.get(`${API}/crm/opportunities?limit=200`, opts).catch(() => ({ data: { items: [] } })),
    ]).then(([c, o]) => {
      if (cancelled) return;
      const contacts = c.data?.items || (Array.isArray(c.data) ? c.data : []);
      const opps = o.data?.items || (Array.isArray(o.data) ? o.data : []);
      const pipeline = opps
        .filter(op => op.stage !== 'abandonne' && op.stage !== 'acte')
        .reduce((s, op) => s + (op.amount_eur || 0), 0);
      setKpis({
        leads: contacts.filter(c => c.status !== 'archived').length,
        pipeline,
        opps: opps.length,
      });
    });
    return () => { cancelled = true; };
  }, []);

  const totalLots = useMemo(() => projects.reduce((s, p) => s + (p.lots_count || 0), 0), [projects]);
  const activeProjects = projects.length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects.filter(p => {
      // status filter
      const isPublished = p.published || p.status === 'final';
      const okFilter = filter === 'all' || (filter === 'published' ? isPublished : !isPublished);
      if (!okFilter) return false;
      // text filter
      if (!q) return true;
      return (p.name || '').toLowerCase().includes(q) || (p.address || '').toLowerCase().includes(q);
    });
  }, [projects, search, filter]);

  // Auto-expand the selected card so clicking a pin reveals project info
  useEffect(() => {
    if (selectedId) setExpandedId(selectedId);
  }, [selectedId]);

  if (!visible) return null;

  return (
    <aside
      className="hud-fade-in flex flex-col shrink-0 absolute top-0 right-0 bottom-0 z-[460] w-full sm:w-[320px] xl:w-[360px]"
      style={{
        background: 'color-mix(in srgb, var(--c-shell) 92%, transparent)',
        borderLeft: '1px solid var(--c-border)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: 'var(--shadow-panel)',
      }}
      data-testid="portfolio-panel"
    >
      {/* Header */}
      <div className="px-4 pt-3 pb-2" style={{ borderBottom: '1px solid var(--c-border)' }}>
        <div className="flex items-center justify-between">
          <span className="hud-eyebrow hud-eyebrow-gold">Portfolio</span>
          {onClose && (
            <button
              onClick={onClose}
              className="hud-btn hud-btn-ghost hud-btn-sm aspect-square px-0"
              data-testid="portfolio-panel-close"
              aria-label="Réduire le portfolio"
              title="Réduire le portfolio"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {/* Tabs : Projets (défaut) / Contrôle */}
        <div
          className="mt-2 grid grid-cols-2 rounded-lg overflow-hidden"
          style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
          role="tablist"
          aria-label="Portfolio sections"
        >
          {[
            { key: 'projets',  label: 'Projets',         icon: Layers },
            { key: 'controle', label: 'Tour de contrôle', icon: Gauge },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.key)}
                data-testid={`portfolio-tab-${t.key}`}
                className="flex items-center justify-center gap-1.5 py-1.5 text-[11.5px] font-semibold tracking-wide transition-colors"
                style={{
                  background: active ? 'rgba(217,160,91,0.18)' : 'transparent',
                  color: active ? 'var(--c-gold)' : 'var(--c-text-muted)',
                  borderRight: t.key === 'projets' ? '1px solid var(--c-border)' : 'none',
                }}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'controle' && (
        <>
          {/* KPI Grid (Top) avec sparklines */}
          <div className="px-3 pt-3 grid grid-cols-2 gap-2" data-testid="portfolio-kpis">
            <Kpi icon={Layers}      label="Projets actifs" value={activeProjects}        trend={trends.projects} />
            <Kpi icon={Building2}   label="Lots"           value={totalLots}             trend={trends.lots} />
            <Kpi icon={TrendingUp}  label="Pipeline CRM"   value={fmtMoney(kpis.pipeline)} tone="gold" trend={trends.pipeline} />
            <Kpi icon={Users}       label="Leads"          value={kpis.leads}            trend={trends.leads} />
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            <p className="text-[11px] leading-relaxed" style={{ color: 'var(--c-text-muted)' }}>
              Vue agrégée portfolio sur 7 jours. Cliquez sur l'onglet <strong>Projets</strong> pour parcourir les opérations en cours.
            </p>
            {selectedId && onDeselectProject && (
              <button
                type="button"
                onClick={onDeselectProject}
                className="w-full text-[11px] py-1.5 rounded-md font-semibold transition-colors flex items-center justify-center gap-1.5"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  color: 'var(--c-text-muted)',
                  border: '1px solid var(--c-border)',
                }}
                data-testid="portfolio-deselect"
              >
                <Crosshair className="w-3 h-3" />
                Désélectionner · vue d'ensemble
              </button>
            )}
          </div>
        </>
      )}

      {tab === 'projets' && (
        <>
          {/* Search + Filter pills */}
          <div className="px-3 pt-3 space-y-2">
            <div
              className="flex items-center gap-2 rounded-lg px-2.5"
              style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)', height: 34 }}
            >
              <Search className="w-3.5 h-3.5" style={{ color: 'var(--c-text-muted)' }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filtrer la liste…"
                className="flex-1 bg-transparent text-[12.5px] outline-none"
                style={{ color: 'var(--c-text)' }}
                data-testid="portfolio-search"
              />
              {search && (
                <button onClick={() => setSearch('')} aria-label="Effacer">
                  <X className="w-3 h-3" style={{ color: 'var(--c-text-muted)' }} />
                </button>
              )}
            </div>
            <div className="flex gap-1.5">
              {[
                { key: 'all',       label: 'Tous' },
                { key: 'active',    label: 'En cours' },
                { key: 'published', label: 'Publié' },
              ].map(f => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter?.(f.key)}
                  className="flex-1 text-[11px] py-1.5 rounded-md transition-colors font-semibold"
                  style={{
                    background: filter === f.key ? 'rgba(217,160,91,0.18)' : 'rgba(255,255,255,0.04)',
                    color: filter === f.key ? 'var(--c-gold)' : 'var(--c-text-muted)',
                    border: filter === f.key ? '1px solid rgba(217,160,91,0.32)' : '1px solid var(--c-border)',
                  }}
                  data-testid={`portfolio-filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {selectedId && onDeselectProject && (
              <button
                type="button"
                onClick={onDeselectProject}
                className="w-full text-[11px] py-1.5 rounded-md font-semibold transition-colors flex items-center justify-center gap-1.5"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  color: 'var(--c-text-muted)',
                  border: '1px solid var(--c-border)',
                }}
                data-testid="portfolio-deselect-projets"
              >
                <Crosshair className="w-3 h-3" />
                Désélectionner · vue d'ensemble
              </button>
            )}
          </div>

          {/* Listings */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5" data-testid="portfolio-listings">
            <div className="flex items-center justify-between mb-1 px-1">
              <span className="hud-eyebrow">Listings</span>
              <span className="hud-eyebrow hud-tabular">{filtered.length}</span>
            </div>
            {filtered.length === 0 && (
              <div className="rounded-xl border border-dashed flex flex-col items-center justify-center py-8 px-3 text-center"
                   style={{ borderColor: 'rgba(255,255,255,0.10)' }}>
                <Layers className="w-5 h-5 mb-1.5" style={{ color: 'var(--c-text-faint)' }} />
                <p className="text-[12px]" style={{ color: 'var(--c-text-muted)' }}>
                  {search ? 'Aucun projet ne correspond.' : 'Aucun projet pour l\'instant.'}
                </p>
              </div>
            )}
            {filtered.map((p) => (
              <ProjectListingCard
                key={p.project_id}
                project={p}
                selected={p.project_id === selectedId}
                expanded={expandedId === p.project_id}
                onSelect={() => { setExpandedId(p.project_id); onSelectProject?.(p); }}
                onCollapse={() => setExpandedId(null)}
                onLocate={() => onSelectProject?.(p)}
                onNavigateDetails={() => onNavigateDetails?.(p)}
                onNavigateMinisite={() => onNavigateMinisite?.(p)}
              />
            ))}
          </div>
        </>
      )}

      {/* Footer */}
      <div className="px-3 py-2.5 flex items-center justify-between gap-2"
           style={{ borderTop: '1px solid var(--c-border)' }}>
        <Link to="/projects" className="hud-btn hud-btn-ghost hud-btn-sm" data-testid="portfolio-all-projects">
          Tous les projets
          <ArrowRight className="w-3 h-3" />
        </Link>
        <button
          type="button"
          onClick={() => navigate('/crm')}
          className="hud-btn hud-btn-ghost hud-btn-sm"
          data-testid="portfolio-crm-invite"
          title="Inviter un contact (via le CRM)"
        >
          <UserPlus className="w-3 h-3" />
          CRM
        </button>
      </div>
    </aside>
  );
};

const Kpi = ({ icon: Icon, label, value, tone = 'neutral', trend }) => (
  <div
    className="rounded-xl p-2.5 transition-colors"
    style={{
      background: 'var(--c-surface-1)',
      border: '1px solid var(--c-border)',
    }}
  >
    <div className="flex items-start justify-between mb-1">
      <Icon className="w-3.5 h-3.5" style={{ color: tone === 'gold' ? 'var(--c-gold)' : 'var(--c-text-muted)' }} strokeWidth={1.75} />
      <span className="hud-eyebrow text-right" style={{ fontSize: 8.5 }}>{label}</span>
    </div>
    <p className="hud-tabular font-semibold leading-none"
       style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}>
      {value}
    </p>
    {trend && trend.length >= 2 && (
      <KpiSparkline data={trend} tone={tone} height={18} />
    )}
  </div>
);

const HEALTH_COLORS = {
  green: { dot: '#22C55E', glow: 'rgba(34,197,94,0.55)', label: 'Saine — tous les lots livrés' },
  amber: { dot: '#F59E0B', glow: 'rgba(245,158,11,0.55)', label: 'En cours' },
  red:   { dot: '#EF4444', glow: 'rgba(239,68,68,0.55)', label: 'À démarrer — aucun fichier' },
  gray:  { dot: 'rgba(255,255,255,0.25)', glow: 'none', label: 'Pas de lots configurés' },
};

const ProjectListingCard = ({ project, selected, expanded, onSelect, onCollapse, onLocate, onNavigateDetails, onNavigateMinisite }) => {
  const hasCoords = Array.isArray(project.coords) && project.coords.length === 2;
  const isPublished = project.status === 'final' || project.published;
  const isOpen = !!expanded;
  const health = HEALTH_COLORS[project.health_status] || HEALTH_COLORS.gray;
  const meta = project.health_meta || {};
  const healthTitle = `${health.label}${meta.total_lots
    ? ` · ${meta.lots_with_gen || 0}/${meta.total_lots} livrés · ${meta.lots_with_plan || 0}/${meta.total_lots} avec plan retenu`
    : ''}`;
  return (
    <div
      data-testid={`portfolio-card-${project.project_id}`}
      data-selected={selected ? '1' : '0'}
      className="rounded-lg overflow-hidden transition-all duration-200"
      style={{
        background: selected ? 'rgba(217,160,91,0.10)' : 'var(--c-surface-1)',
        border: `1px solid ${selected ? 'var(--c-border-accent)' : 'var(--c-border)'}`,
        boxShadow: selected ? '0 0 10px rgba(217,160,91,0.15)' : 'none',
      }}
    >
      <button
        type="button"
        onClick={onSelect}
        className="w-full text-left group"
        aria-expanded={isOpen}
        data-testid={`portfolio-card-row-${project.project_id}`}
      >
        {/* === Single-line row: avatar 24 · name · address · lots · chevron === */}
        <div className="flex items-center gap-2 px-2.5 py-2">
          <div
            className="w-7 h-7 rounded-md shrink-0 relative overflow-hidden"
            style={{
              background: project.thumbnail_url
                ? `url(${project.thumbnail_url}) center/cover`
                : `linear-gradient(135deg, ${stringToColor(project.name, 0)} 0%, ${stringToColor(project.name, -10)} 100%)`,
              border: '1px solid var(--c-border)',
            }}
          >
            {!project.thumbnail_url && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span
                  className="text-[9.5px] font-bold tracking-[0.12em]"
                  style={{ color: 'rgba(255,255,255,0.85)' }}
                >
                  {(project.name || '?').slice(0, 2).toUpperCase()}
                </span>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-[12.5px] font-semibold truncate"
                 style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)' }}>
                {project.name}
              </p>
              {isPublished && (
                <span className="hud-badge hud-badge-success" style={{ padding: '0 5px', fontSize: 8 }}>
                  Publié
                </span>
              )}
            </div>
            <p className="text-[10.5px] truncate mt-px" style={{ color: 'var(--c-text-muted)' }}>
              {project.address || (hasCoords ? `${project.coords[0].toFixed(2)}, ${project.coords[1].toFixed(2)}` : 'Sans géolocalisation')}
            </p>
          </div>
          <div className="flex flex-col items-end shrink-0 pr-1">
            <span className="hud-tabular text-[11px] font-semibold" style={{ color: 'var(--c-text)' }}>
              {(project.lots_count || 0)}
            </span>
            <span className="text-[8.5px] uppercase tracking-wider" style={{ color: 'var(--c-text-faint)' }}>
              lots
            </span>
          </div>
          {/* Health dot — consolidates CCTP/plans/generations */}
          <span
            className="shrink-0 inline-flex items-center"
            title={healthTitle}
            data-testid={`portfolio-health-${project.project_id}`}
            data-health={project.health_status || 'gray'}
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{
                background: health.dot,
                boxShadow: health.glow !== 'none' ? `0 0 8px ${health.glow}` : 'none',
              }}
            />
          </span>
          {isOpen
            ? <ChevronUp className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--c-text-faint)' }} />
            : <ChevronDown className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--c-text-faint)' }} />
          }
        </div>
      </button>

      {/* Expanded info + action buttons */}
      {isOpen && (
        <div
          className="px-2.5 pb-2.5 pt-2 space-y-2"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
          data-testid={`portfolio-card-expanded-${project.project_id}`}
        >
          {project.description && (
            <p className="text-[11px]" style={{ color: 'var(--c-text-muted)' }}>
              {project.description}
            </p>
          )}
          {project.opportunities_amount > 0 && (
            <div className="flex items-center justify-between text-[10.5px]" style={{ color: 'var(--c-text-faint)' }}>
              <span className="hud-eyebrow">Opportunités</span>
              <span className="hud-tabular font-semibold" style={{ color: 'var(--c-gold)' }}>
                {fmtMoney(project.opportunities_amount)}
              </span>
            </div>
          )}
          {/* Health KPI strip */}
          {meta.total_lots > 0 && (
            <div className="grid grid-cols-3 gap-1.5 text-center" data-testid={`portfolio-health-meta-${project.project_id}`}>
              <HealthKpi label="Plans" value={`${meta.lots_with_plan || 0}/${meta.total_lots}`} />
              <HealthKpi label="Livrés" value={`${meta.lots_with_gen || 0}/${meta.total_lots}`} tone={meta.lots_with_gen === meta.total_lots ? 'green' : 'gold'} />
              <HealthKpi label="Fichiers" value={meta.files || 0} />
            </div>
          )}
          {(project.date_livraison || project.date_debut_commercialisation) && (
            <div className="grid grid-cols-2 gap-2 text-[10.5px]" style={{ color: 'var(--c-text-faint)' }}>
              {project.date_debut_commercialisation && (
                <div>
                  <span className="hud-eyebrow">Commercialisation</span>
                  <p className="hud-tabular mt-0.5">{project.date_debut_commercialisation}</p>
                </div>
              )}
              {project.date_livraison && (
                <div>
                  <span className="hud-eyebrow">Livraison</span>
                  <p className="hud-tabular mt-0.5">{project.date_livraison}</p>
                </div>
              )}
            </div>
          )}
          <div className="flex gap-1.5 pt-1">
            {hasCoords && (
              <button
                type="button"
                onClick={onLocate}
                className="flex-1 hud-btn hud-btn-ghost hud-btn-sm"
                data-testid={`portfolio-card-locate-${project.project_id}`}
                title="Centrer la carte"
              >
                <Crosshair className="w-3 h-3" />
                Localiser
              </button>
            )}
            <button
              type="button"
              onClick={onNavigateDetails}
              className="flex-1 hud-btn hud-btn-ghost hud-btn-sm"
              data-testid={`portfolio-card-details-${project.project_id}`}
            >
              <Eye className="w-3 h-3" />
              Détails
            </button>
            <button
              type="button"
              onClick={onNavigateMinisite}
              className="flex-1 hud-btn hud-btn-primary hud-btn-sm"
              data-testid={`portfolio-card-minisite-${project.project_id}`}
              title="Voir le mini-site public"
            >
              <ArrowRight className="w-3 h-3" />
              Mini-site
            </button>
          </div>
          {selected && (
            <button
              type="button"
              onClick={onCollapse}
              className="w-full text-[10.5px] uppercase tracking-[0.16em] font-semibold py-1 transition-colors"
              style={{ color: 'var(--c-text-faint)' }}
              data-testid={`portfolio-card-collapse-${project.project_id}`}
            >
              Refermer
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Helpers
function fmtMoney(n) {
  if (!n || n < 0) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M€`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)} k€`;
  return `${n} €`;
}
function stringToColor(s = '', shift = 0) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  const l = 28 + shift;
  return `hsl(${(h + 30) % 360}, 35%, ${Math.max(15, Math.min(60, l))}%)`;
}

const HealthKpi = ({ label, value, tone = 'neutral' }) => (
  <div
    className="rounded-md px-1.5 py-1"
    style={{
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.06)',
    }}
  >
    <p
      className="hud-tabular text-[11.5px] font-semibold leading-tight"
      style={{ color: tone === 'green' ? '#22C55E' : tone === 'gold' ? 'var(--c-gold)' : 'var(--c-text)' }}
    >
      {value}
    </p>
    <p className="hud-eyebrow" style={{ fontSize: 8.5 }}>
      {label}
    </p>
  </div>
);

export default PortfolioPanel;
